import { CellValueChangedEvent, ColDef, GetRowIdParams, GridReadyEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AgFns, GridOptionsExt, BaseService, ISortModel, SaveAndQueryComponent, wrapBusy } from '@core';
import { EntityFns } from '@data';
import { StringFns, UtilFns } from '@utils';
import * as XLSX from 'xlsx';
import { Guid } from 'guid-typescript';
import * as FileSaver from 'file-saver';
import * as Excel from 'exceljs';
import {
  Account,
  AccountImportCodeOptionEnum,
  AccountUser,
  ActiveStatusEnum,
  GenderEnum,
  ImportUser,
  ImportUserPlanRequestRow,
  ProgramUserGroup,
  ProgramUserGroupMap,
  ProximityRole,
  ProximityUser,
  ProximityUserNote,
  ShippingUserGroup,
  ShippingUserGroupMap,
} from '@models';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { ConfirmationService } from 'primeng/api';

// https://github.com/exceljs/exceljs?tab=readme-ov-file#writing-xlsx

export class ShippingCode {
  code = '';
  shippingUserGroupId = '';
}

export class ProgramCode {
  code = '';
  programUserGroupId = '';
}

/**
 * Exactly what the import will do to one matched row — decided once by recomputePlans(),
 * then read by the pre-submit summary AND by the save.
 *
 * Sharing one record between the three is deliberate. If the save re-derived the rules for
 * itself, the Details column could promise one thing and the database receive another; the
 * admin would have approved a summary that did not describe the save.
 */
interface RowChanges {
  /** Rule 3 — the ApiLink to write, when the existing record has none. */
  setApiLink?: string;
  /** Rule 4 — the Hire Date to write, when the existing record has none. */
  setHireDate?: Date;
  /** Rule 5 — the Shipping Group to move to. */
  moveShippingTo?: { id: string; name: string };
  /** Rule 5 — the memberships the move removes. */
  removeShippingGroupIds: string[];
  /** Rule 6 — the Program Group to attach. Never a removal. */
  joinProgram?: { id: string; name: string };
  /** A different ApiLink already on record; kept, not overwritten. */
  keptApiLink?: boolean;
  /** A different Hire Date already on record; kept, not overwritten. */
  keptHireDate?: boolean;
}

@Component({
  selector: 'prox-import-users',
  templateUrl: './import-users.component.html',
  standalone: false,
  styles: [
    `
      /* Per-row outcomes shown after the admin clicks Submit. The QuickBooks theme has no
         row-state palette beyond qb-error-row (yellow), which is used for pre-submit
         rejections, so the four post-save colors are defined here. Flagged to the
         developer as a QB theme gap rather than added to the shared theme files. */
      :host ::ng-deep .qb-import-added .ag-cell {
        background-color: #d4edda;
      } /* green — a new Account User */
      :host ::ng-deep .qb-import-updated .ag-cell {
        background-color: #d6e4f7;
      } /* blue — an existing Account User changed */
      :host ::ng-deep .qb-import-skipped .ag-cell {
        background-color: #e2e3e5;
      } /* grey — never sent */
      :host ::ng-deep .qb-import-failed .ag-cell {
        background-color: #f8d7da;
      } /* red — sent and refused */
    `,
  ],
})
export class ImportUsersComponent extends SaveAndQueryComponent {
  accountId?: string;
  account?: Account;
  importUsers: ImportUser[] = [];
  importRunning = false;
  /** Gates Submit and Upload against double-clicks during parse, plan, or save. */
  inFlight = false;

  /**
   * Which code dimensions this account uses, from Account.accountImportCodeOptionId.
   * These drive the template columns, the grid columns, the mapping grids and the submit
   * gate together, so the page cannot offer a dimension the account does not use.
   */
  includeShippingCodes = false;
  includeProgramCodes = false;
  get includeCodes() {
    return this.includeShippingCodes || this.includeProgramCodes;
  }

  accountUserRoles: ProximityRole[] = [];
  /** ProximityRole.id for the AccountUser role on this account. */
  defaultAccountUserRoleId = '';

  gridOptions!: GridOptionsExt;
  shippingCodesGridOptions!: GridOptionsExt;
  programCodesGridOptions!: GridOptionsExt;

  shippingUserGroups: ShippingUserGroup[] = [];
  programUserGroups: ProgramUserGroup[] = [];
  /**
   * What the code-mapping dropdown offers. Inactive groups are held back: one pick here maps
   * EVERY imported row carrying that code, so a single mis-pick attaches a whole cohort to a
   * group that routes no orders. The server refuses an Inactive PUG membership outright
   * (CheckProgramUserGroupMapAdds), so filtering here keeps the save from failing as well.
   * The full list above stays intact for name lookup.
   * Hold and Phaseout remain selectable. Hold is by design a temporary state, so an import
   * into a held group is early rather than dead; Phaseout is a valid state for a PUG.
   */
  selectableProgramUserGroups: ProgramUserGroup[] = [];

  shippingCodes: ShippingCode[] = [];
  programCodes: ProgramCode[] = [];

  /** Keyed by ImportUser.Id. The single source of truth for what each Update row changes. */
  private rowChanges = new Map<string, RowChanges>();

  rowClassRules = {
    // After a save attempt, the outcome owns the color.
    'qb-import-added': (params: any) => params.data?.Outcome === 'Added',
    'qb-import-updated': (params: any) => params.data?.Outcome === 'Updated',
    'qb-import-skipped': (params: any) => params.data?.Outcome === 'Skipped',
    'qb-import-failed': (params: any) => params.data?.Outcome === 'Failed',
    // Before submit, only rejections are highlighted. 'No Change' stays white on purpose.
    'qb-error-row': (params: any) => !params.data?.Outcome && params.data?.Plan === 'Reject',
  };

  acceptedFiles = '.xls, .xlsx, .csv';
  blobType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'AccountId');

    this.account = await this.dbQueryService.getAccountById(this.accountId);
    UtilFns.assertNonNull(this.account, 'Account');

    const codeMode = this.account.accountImportCodeOptionId;
    this.includeShippingCodes =
      codeMode === AccountImportCodeOptionEnum.Shipping || codeMode === AccountImportCodeOptionEnum.Both;
    this.includeProgramCodes =
      codeMode === AccountImportCodeOptionEnum.Program || codeMode === AccountImportCodeOptionEnum.Both;

    this.accountUserRoles = await this.dbQueryService.getAccountUserRoles(this.accountId);
    UtilFns.assertArrayNotEmpty(this.accountUserRoles, 'Account User Roles');
    // Hard-fail rather than guess. This used to fall back to the first role in name order,
    // which silently imported an entire batch under whatever role happened to sort first.
    const accountUserRole = this.accountUserRoles.find((r) => (r.name || '').toLowerCase() === 'accountuser');
    if (!accountUserRole) {
      await this.dialogService.okDialog(
        'Cannot Import',
        `This account has no Proximity Role named <b>AccountUser</b>, so the import cannot decide ` +
          `what role to give imported people. Add that role to the account, then try again.`,
      );
      this.navigateBack();
      return;
    }
    this.defaultAccountUserRoleId = accountUserRole.id;

    if (this.includeShippingCodes) {
      this.shippingUserGroups = await this.dbQueryService.getShippingUserGroups(this.accountId);
      this.shippingUserGroups.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    if (this.includeProgramCodes) {
      this.programUserGroups = await this.dbQueryService.getProgramUserGroups(this.accountId);
      this.programUserGroups.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      this.selectableProgramUserGroups = this.programUserGroups.filter(
        (p) => p.activeStatusId !== ActiveStatusEnum.Inactive,
      );
    }

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (rowIdParams: GetRowIdParams) => (rowIdParams.data as ImportUser).Id,
      rowModelType: 'clientSide',
    });

    this.shippingCodesGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.shippingCodesOnGridReady,
      onCellValueChanged: this.onShippingGroupCellValueChanged,
      getRowId: (rowIdParams: GetRowIdParams) => (rowIdParams.data as ShippingCode).code,
      rowModelType: 'clientSide',
    });

    this.programCodesGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.programCodesOnGridReady,
      onCellValueChanged: this.onProgramGroupCellValueChanged,
      getRowId: (rowIdParams: GetRowIdParams) => (rowIdParams.data as ProgramCode).code,
      rowModelType: 'clientSide',
    });
    this.setTitle('Account User Import for ' + this.account.name);
    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.applyGridRouteParams(this.gridOptions);
  }

  // #region Code mapping

  getShippingGroupName(id: string) {
    return this.shippingUserGroups.find((p) => p.id == id)?.name;
  }

  onShippingGroupCellValueChanged(event: CellValueChangedEvent) {
    const name = this.getShippingGroupName(event.data.shippingUserGroupId);
    this.importUsers.forEach((p) => {
      if (p.ShippingUserGroupCode == event.data.code) {
        p.ShippingUserGroupId = event.data.shippingUserGroupId;
        p.ShippingUserGroupName = name;
      }
    });
    this.recomputePlans();
  }

  getProgramGroupName(id: string) {
    return this.programUserGroups.find((p) => p.id == id)?.name;
  }

  onProgramGroupCellValueChanged(event: CellValueChangedEvent) {
    const name = this.getProgramGroupName(event.data.programUserGroupId);
    this.importUsers.forEach((p) => {
      if (p.ProgramUserGroupCode == event.data.code) {
        p.ProgramUserGroupId = event.data.programUserGroupId;
        p.ProgramUserGroupName = name;
      }
    });
    this.recomputePlans();
  }

  shippingCodesOnGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { headerName: 'Code', field: 'code', width: 100, sortable: false, editable: false },
      {
        headerName: 'Shipping User Group',
        editable: true,
        ...AgFns.createDropdownEditor('shippingUserGroupId', this.shippingUserGroups),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'code', sort: 'asc' }] as ISortModel);
  }

  programCodesOnGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      { headerName: 'Code', field: 'code', width: 100, sortable: false, editable: false },
      {
        headerName: 'Program User Group',
        editable: true,
        ...AgFns.createDropdownEditor('programUserGroupId', this.selectableProgramUserGroups),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'code', sort: 'asc' }] as ISortModel);
  }

  loadCodes() {
    this.shippingCodes = [];
    this.programCodes = [];

    if (this.includeShippingCodes) {
      [...new Set(this.importUsers.map((u) => (u.ShippingUserGroupCode || '').trim()))]
        .filter((code) => !!code)
        .forEach((code) => {
          const c = new ShippingCode();
          c.code = code;
          this.shippingCodes.push(c);
        });
    }

    if (this.includeProgramCodes) {
      [...new Set(this.importUsers.map((u) => (u.ProgramUserGroupCode || '').trim()))]
        .filter((code) => !!code)
        .forEach((code) => {
          const c = new ProgramCode();
          c.code = code;
          this.programCodes.push(c);
        });
    }
  }

  // #endregion

  getColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      { headerName: 'Plan', field: 'Plan', filter: 'agTextColumnFilter', ...AgFns.fixWidth(110) },
      {
        headerName: 'Outcome',
        colId: 'Outcome',
        field: 'Outcome',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(110),
        hide: true, // shown only once a save has been attempted
      },
      { headerName: 'Details', colId: 'Details', field: 'Details', ...AgFns.flexWidth(300) },
      { headerName: 'Last Name', field: 'LastName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      { headerName: 'First Name', field: 'FirstName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      { headerName: 'Middle Name', field: 'MiddleName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(100) },
      { headerName: 'Salutation', field: 'Salutation', filter: 'agTextColumnFilter', ...AgFns.fixWidth(120) },
      { headerName: 'Gender', field: 'Gender', filter: 'agTextColumnFilter', ...AgFns.fixWidth(120) },
      { headerName: 'User Name', field: 'UserName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(200) },
      { headerName: 'Email', field: 'Email', filter: 'agTextColumnFilter', ...AgFns.flexWidth(200) },
      { headerName: 'ApiLink', field: 'ApiLink', filter: 'agTextColumnFilter', ...AgFns.flexWidth(150) },
      { headerName: 'Hire Date', field: 'HireDate', filter: 'agDateColumnFilter', ...AgFns.fixWidth(120) },
      {
        headerName: 'Shipping Group Code',
        field: 'ShippingUserGroupCode',
        filter: 'agTextColumnFilter',
        hide: !this.includeShippingCodes,
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Shipping Group Name',
        field: 'ShippingUserGroupName',
        filter: 'agTextColumnFilter',
        hide: !this.includeShippingCodes,
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Program Group Code',
        field: 'ProgramUserGroupCode',
        filter: 'agTextColumnFilter',
        hide: !this.includeProgramCodes,
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Program Group Name',
        field: 'ProgramUserGroupName',
        filter: 'agTextColumnFilter',
        hide: !this.includeProgramCodes,
        ...AgFns.flexWidth(150),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'LastName', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  private updateColumnVisibility() {
    const api = this.gridOptions.apiX;
    if (!api) return;
    api.setColumnsVisible(['Outcome'], this.importUsers.some((u) => !!u.Outcome));
  }

  // #region Template download

  async getTemplate() {
    await this.exportAsExcelFile('UsersTemplate.xlsx');
  }

  // https://www.codeproject.com/Tips/1251189/Excel-Export-from-Angular-2plus

  async exportAsExcelFile(excelFileName: string): Promise<void> {
    const workbook = new Excel.Workbook();
    workbook.addWorksheet('AccountUsers', { views: [{ activeCell: 'A1' }] });
    const sheet: Excel.Worksheet = <Excel.Worksheet>workbook.getWorksheet(1);

    // Each column carries the header name, key, width, and a `note` shown as a
    // hover tooltip in Excel so admins know what to fill into each field.
    interface ImportColumn {
      header: string;
      key: string;
      width: number;
      note: string;
    }
    const columns: ImportColumn[] = [
      { header: 'LastName', key: 'LastName', width: 30, note: 'REQUIRED. Must start with a letter.' },
      { header: 'FirstName', key: 'FirstName', width: 30, note: 'REQUIRED. Must start with a letter.' },
      { header: 'MiddleName', key: 'MiddleName', width: 30, note: 'Optional. If present, must start with a letter.' },
      { header: 'Salutation', key: 'Salutation', width: 10, note: 'Optional (e.g. Mr., Ms., Dr.).' },
      { header: 'Gender', key: 'Gender', width: 10, note: 'Optional. Must be blank, M, F, Male, or Female.' },
      {
        header: 'Email',
        key: 'Email',
        width: 30,
        note:
          'REQUIRED. A valid email address. This is how the import recognises a person: an email ' +
          'already on this account makes the row an update rather than a new user.',
      },
      {
        header: 'ApiLink',
        key: 'ApiLink',
        width: 30,
        note:
          'Optional external identifier used by Proximity API integrations. Must be unique within ' +
          'this upload, and must not already belong to a different person on this account.',
      },
      {
        header: 'HireDate',
        key: 'HireDate',
        width: 30,
        note: 'Optional. If present, must be a recognizable date (e.g. MM/DD/YYYY).',
      },
    ];
    if (this.includeShippingCodes) {
      columns.push({
        header: 'ShippingUserGroupCode',
        key: 'ShippingUserGroupCode',
        width: 30,
        note:
          'A code identifying the shipping group; after upload, attach each unique code to a real ' +
          'Shipping User Group. An existing person whose code names a different group is MOVED to it.',
      });
    }
    if (this.includeProgramCodes) {
      columns.push({
        header: 'ProgramUserGroupCode',
        key: 'ProgramUserGroupCode',
        width: 30,
        note:
          'A code identifying the program group; after upload, attach each unique code to a real ' +
          'Program User Group. An existing person is ADDED to it; their other program memberships stay.',
      });
    }
    sheet.columns = columns.map(({ header, key, width }) => ({ header, key, width }));

    // Style each header cell with white text on a black solid fill, and attach
    // the format-hint note. ExcelJS requires `fgColor` (not `bgColor`) for
    // `pattern: 'solid'` to actually render the background.
    columns.forEach((col, idx) => {
      const cell = sheet.getRow(1).getCell(idx + 1);
      cell.font = { color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '000000' } };
      cell.note = col.note;
    });

    const data = await workbook.xlsx.writeBuffer();
    const blob = new Blob([data], { type: this.blobType });
    FileSaver.saveAs(blob, excelFileName);
  }

  // #endregion

  // #region Upload and parse

  //https://stackoverflow.com/questions/76488660/how-can-i-upload-and-parse-my-local-excel-inside-an-angular-component-when-it-l

  // https://stackoverflow.com/questions/55370778/i-am-converting-excel-sheet-to-json-objects-while-converting-date-from-excel-is

  /** Header columns that must be present in the uploaded spreadsheet. */
  private readonly REQUIRED_COLUMNS = ['LastName', 'FirstName', 'Email'];

  async onLoad(e: any) {
    const reader = new FileReader();
    this.inFlight = true;
    reader.readAsArrayBuffer(e.files[0]);

    reader.onloadend = async (ev) => {
      try {
        const data = ev.target?.result;
        const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' });

        // Locate the first non-empty sheet and validate its header row before parsing rows.
        // This catches "wrong file" and "renamed column" cases up front, before per-row
        // validation reports the same generic error N times.
        let chosenSheet: XLSX.WorkSheet | undefined;
        for (const sheetName of workbook.SheetNames) {
          const sheet = workbook.Sheets[sheetName];
          const headerOnly = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, blankrows: false });
          if (headerOnly.length > 0) {
            chosenSheet = sheet;
            const headers = (headerOnly[0] || []).map((h) => (h || '').toString().trim());
            const missing = this.REQUIRED_COLUMNS.filter((req) => !headers.includes(req));
            if (missing.length > 0) {
              await this.dialogService.warnDialog(
                'Spreadsheet Column Mismatch',
                `The uploaded file is missing required column${missing.length === 1 ? '' : 's'}: ` +
                  `<b>${missing.join(', ')}</b>.<br/><br/>` +
                  `Download the template via the <b>Download Template</b> button to see the expected column names exactly.`,
              );
              this.importUsers = [];
              return;
            }
            break;
          }
        }
        if (!chosenSheet) {
          await this.dialogService.warnDialog('Empty Spreadsheet', 'The uploaded file does not contain any data rows.');
          this.importUsers = [];
          return;
        }

        // Every value is trimmed on the way in. Before, only the validation DTO trimmed, so a
        // trailing space reached the database and broke later exact-match comparisons.
        this.importUsers = XLSX.utils.sheet_to_json<ImportUser>(chosenSheet).map((c) => {
          const rec = new ImportUser();
          rec.Id = Guid.create().toString();
          rec.LastName = this.str(c.LastName);
          rec.FirstName = this.str(c.FirstName);
          rec.MiddleName = this.str(c.MiddleName);
          rec.Salutation = this.str(c.Salutation);
          rec.Gender = this.str(c.Gender);
          rec.Email = this.str(c.Email);
          rec.ApiLink = this.str(c.ApiLink);
          rec.HireDate = c.HireDate;
          rec.ShippingUserGroupCode = this.str(c.ShippingUserGroupCode);
          rec.ProgramUserGroupCode = this.str(c.ProgramUserGroupCode);
          return rec;
        });

        this.loadCodes();
        await this.planImport();
      } finally {
        this.inFlight = false;
      }
    };
  }

  /** Trim a spreadsheet value to a string; blank for null, undefined or non-text. */
  private str(value: any): string {
    if (value == null) return '';
    return value.toString().trim();
  }

  // #endregion

  // #region Planning

  /**
   * Two passes. Client-side checks first — required fields, formats, duplicates inside the
   * file — then a single server call that decides identity for every surviving row and
   * returns the existing state an update needs. recomputePlans() turns that into the
   * per-row Plan and Details the admin reads.
   */
  async planImport(): Promise<void> {
    if (this.importUsers.length === 0) {
      this.toastr.warning('No records');
      return;
    }

    this.rowChanges.clear();
    this.importUsers.forEach((u) => {
      u.Plan = '';
      u.Details = '';
      u.Outcome = '';
    });

    this.runClientChecks();

    const rows: ImportUserPlanRequestRow[] = this.importUsers
      .filter((u) => u.Plan !== 'Reject')
      .map((u) => {
        const row = new ImportUserPlanRequestRow();
        row.ImportId = u.Id;
        row.FirstName = u.FirstName;
        row.LastName = u.LastName;
        row.Email = u.Email;
        row.ApiLink = u.ApiLink ?? '';
        return row;
      });

    if (rows.length > 0) {
      const plans = await this.dbSaveService.postPlanAccountUserImport(this.accountId!, rows);
      plans.forEach((p) => {
        const u = this.importUsers.find((x) => x.Id === p.ImportId);
        if (!u) return;
        if (p.Decision === 'Reject') {
          u.Plan = 'Reject';
          u.Details = p.RejectReason;
          return;
        }
        if (p.Decision === 'Add') {
          u.UserName = p.UserName;
          u.Plan = 'Add';
          return;
        }
        // Match — carry the existing state across for the delta rules.
        u.MatchedAccountUserId = p.MatchedAccountUserId;
        u.MatchedProximityUserId = p.MatchedProximityUserId;
        u.MatchedDisplayName = p.MatchedDisplayName;
        u.ExistingApiLink = p.ExistingApiLink ?? '';
        // JSON hands dates back as strings regardless of the DTO's declared type.
        u.ExistingHireDate = p.ExistingHireDate ? new Date(p.ExistingHireDate as any) : undefined;
        u.ExistingShippingGroups = p.ExistingShippingGroups ?? [];
        u.ExistingProgramGroups = p.ExistingProgramGroups ?? [];
      });
    }

    this.recomputePlans();
  }

  /** Per-batch and per-row checks that need no server. Sets Plan = 'Reject' with a reason. */
  private runClientChecks() {
    const reject = (u: ImportUser, reason: string) => {
      u.Plan = 'Reject';
      u.Details = reason;
    };

    // Duplicates inside the file. Compared case-insensitively and trimmed, because the
    // database compares that way too: two rows reading Bob@x.com and bob@x.com are one
    // person, and letting both through meant SQL caught it only after the first row saved.
    const emailKey = (u: ImportUser) => u.Email.toLowerCase();
    const apiLinkKey = (u: ImportUser) => (u.ApiLink ?? '').toLowerCase();

    const dupEmails = UtilFns.getDuplicates(this.importUsers.filter((u) => u.Email).map(emailKey));
    const dupApiLinks = UtilFns.getDuplicates(this.importUsers.filter((u) => u.ApiLink).map(apiLinkKey));

    this.importUsers.forEach((u) => {
      if (u.Email && dupEmails.includes(emailKey(u))) {
        reject(u, 'This email appears more than once in your file');
        return;
      }
      if (u.ApiLink && dupApiLinks.includes(apiLinkKey(u))) {
        reject(u, 'This ApiLink appears more than once in your file');
        return;
      }
      if (!u.LastName || !u.FirstName || !u.Email) {
        reject(u, 'Last Name, First Name and Email are all required');
        return;
      }
      if (!StringFns.isFirstCharAlpha(u.LastName)) {
        reject(u, 'Last Name must start with a letter');
        return;
      }
      if (!StringFns.isFirstCharAlpha(u.FirstName)) {
        reject(u, 'First Name must start with a letter');
        return;
      }
      if (u.MiddleName && !StringFns.isFirstCharAlpha(u.MiddleName)) {
        reject(u, 'Middle Name must start with a letter');
        return;
      }
      if (!StringFns.isValidEmail(u.Email)) {
        reject(u, 'Invalid email address');
        return;
      }
      if (u.Gender && !this.isRecognizedGender(u.Gender)) {
        reject(u, 'Gender must be blank, M, F, Male, or Female');
        return;
      }
      if (u.HireDate != null && u.HireDate !== '' && !this.parseDate(u.HireDate)) {
        reject(u, 'Hire Date is not a date');
        return;
      }
    });
  }

  /**
   * Turn the server's identity decision plus the current code mappings into the per-row Plan
   * and Details, and record exactly what each Update will change.
   *
   * Cheap and side-effect free apart from the row fields, so it runs again on every code
   * mapping change — that is what makes the grid re-plan as the admin maps a code, instead of
   * only finding out at submit time how many people a mapping was about to move.
   */
  recomputePlans() {
    // Once a save has happened the Plan column is history, not a forecast. Re-planning it
    // would show 'Add' beside an Outcome of 'Added' and invite a second submit of work that
    // is already done. The admin uploads again to start over.
    if (this.importUsers.some((u) => !!u.Outcome)) return;

    this.rowChanges.clear();

    this.importUsers.forEach((u) => {
      if (u.Plan === 'Reject') return;
      if (!u.MatchedAccountUserId) {
        u.Plan = 'Add';
        u.Details = '';
        return;
      }

      const ch: RowChanges = { removeShippingGroupIds: [] };
      const willChange: string[] = [];
      const kept: string[] = [];

      // Rule 3 — ApiLink, filled only when the existing record has none.
      const newApiLink = (u.ApiLink ?? '').trim();
      const oldApiLink = (u.ExistingApiLink ?? '').trim();
      if (newApiLink) {
        if (!oldApiLink) {
          ch.setApiLink = newApiLink;
          willChange.push(`ApiLink will be set to '${newApiLink}'`);
        } else if (oldApiLink.toLowerCase() !== newApiLink.toLowerCase()) {
          ch.keptApiLink = true;
          kept.push(`Kept existing ApiLink '${oldApiLink}' — your file says '${newApiLink}'`);
        }
      }

      // Rule 4 — Hire Date, filled only when the existing record has none.
      const newHireDate = this.parseDate(u.HireDate);
      if (newHireDate) {
        if (!u.ExistingHireDate) {
          ch.setHireDate = newHireDate;
          willChange.push(`Hire Date will be set to ${this.fmtDate(newHireDate)}`);
        } else if (!this.sameDay(u.ExistingHireDate, newHireDate)) {
          ch.keptHireDate = true;
          kept.push(
            `Kept existing Hire Date ${this.fmtDate(u.ExistingHireDate)} — your file says ${this.fmtDate(newHireDate)}`,
          );
        }
      }

      // Rule 5 — Shipping Group is a MOVE. Safe: almost everybody holds exactly one, and
      // removing a Shipping Group membership has no server guard behind it.
      if (this.includeShippingCodes && u.ShippingUserGroupId) {
        const already = u.ExistingShippingGroups.some((g) => g.Id === u.ShippingUserGroupId);
        if (!already) {
          const target = { id: u.ShippingUserGroupId, name: u.ShippingUserGroupName ?? '' };
          ch.moveShippingTo = target;
          ch.removeShippingGroupIds = u.ExistingShippingGroups.map((g) => g.Id);
          const oldNames = u.ExistingShippingGroups.map((g) => g.Name).join(', ');
          willChange.push(
            oldNames
              ? `Shipping Group: ${oldNames} → ${target.name}`
              : `Shipping Group will be set to ${target.name}`,
          );
        }
      }

      // Rule 6 — Program Group is an ATTACH, never a move. A person legitimately belongs to
      // one Program Group per Program, so removing the others would cut off access to
      // Programs the spreadsheet never mentioned. Removals go through the Account User page,
      // which checks for in-flight orders first (CheckProgramUserGroupMapDeletes).
      if (this.includeProgramCodes && u.ProgramUserGroupId) {
        const already = u.ExistingProgramGroups.some((g) => g.Id === u.ProgramUserGroupId);
        if (!already) {
          ch.joinProgram = { id: u.ProgramUserGroupId, name: u.ProgramUserGroupName ?? '' };
          willChange.push(`Will join Program Group ${ch.joinProgram.name}`);
        }
      }

      const matched = `Matched ${u.MatchedDisplayName}`;
      if (willChange.length > 0) {
        u.Plan = 'Update';
        this.rowChanges.set(u.Id, ch);
      } else {
        // A kept-as-is divergence changes nothing, so it is not an Update.
        u.Plan = 'No Change';
      }
      u.Details = [matched, ...willChange, ...kept].join('; ');
    });

    this.updateColumnVisibility();
    this.gridOptions.apiX?.redrawRows();
  }

  /** Allow-list of acceptable Gender values (case-insensitive). */
  private isRecognizedGender(gender: string): boolean {
    const g = (gender || '').trim().toUpperCase();
    return g === 'M' || g === 'F' || g === 'MALE' || g === 'FEMALE';
  }

  /** A real Date, or undefined when the value is blank or unreadable. */
  private parseDate(value: any): Date | undefined {
    if (value == null || value === '') return undefined;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }

  private fmtDate(d: Date): string {
    return d.toLocaleDateString();
  }

  private sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  // #endregion

  // #region Submit

  submitDisabled() {
    return this.importUsers.length == 0 || this.inFlight;
  }

  /** True once a save has been attempted, which is what turns the grid into a results report. */
  get hasResults() {
    return this.importUsers.some((u) => !!u.Outcome);
  }

  /**
   * The run, as a spreadsheet the account can be sent. Exports the columns on screen, so the
   * code columns an account does not use stay out of it, and ag-Grid honours the grid's own
   * filter and sort — an admin who filtered down to the failures exports exactly those.
   */
  onExportResults() {
    const api = this.gridOptions.apiX;
    if (api == null) return;
    const safeName = (this.account?.name || 'account').replace(/[^A-Za-z0-9._-]+/g, '_');
    const stamp = new Date().toISOString().split('T')[0];
    api.exportDataAsExcel({
      fileName: `AccountUserImport_${safeName}_${stamp}.xlsx`,
      sheetName: 'Results',
    });
  }

  async onSubmit() {
    const adds = this.importUsers.filter((u) => u.Plan === 'Add');
    const updates = this.importUsers.filter((u) => u.Plan === 'Update');
    const noChange = this.importUsers.filter((u) => u.Plan === 'No Change');
    const rejects = this.importUsers.filter((u) => u.Plan === 'Reject');

    if (adds.length === 0 && updates.length === 0) {
      await this.dialogService.warnDialog(
        'Nothing To Do',
        rejects.length > 0 && noChange.length === 0
          ? 'Every row has an error. Fix the spreadsheet and upload it again.'
          : 'Nothing in this file changes anything. There is no import to run.',
      );
      return;
    }

    // Code-mapping completeness. Only the dimensions this account uses are required.
    const missing: string[] = [];
    if (this.includeShippingCodes && this.shippingCodes.some((c) => !c.shippingUserGroupId)) missing.push('Shipping');
    if (this.includeProgramCodes && this.programCodes.some((c) => !c.programUserGroupId)) missing.push('Program');
    if (missing.length > 0) {
      await this.dialogService.okDialog(
        'Unable To Submit',
        `All ${missing.join(' and ')} Import Codes must first be applied.`,
      );
      return;
    }

    const changes = updates.map((u) => this.rowChanges.get(u.Id)).filter((c): c is RowChanges => !!c);
    const apiLinkCount = changes.filter((c) => !!c.setApiLink).length;
    const hireDateCount = changes.filter((c) => !!c.setHireDate).length;
    const shippingCount = changes.filter((c) => !!c.moveShippingTo).length;
    const programCount = changes.filter((c) => !!c.joinProgram).length;
    const keptCount = this.importUsers.filter((u) => {
      const c = this.rowChanges.get(u.Id);
      return c?.keptApiLink || c?.keptHireDate;
    }).length;

    const line = (n: number, what: string) => (n > 0 ? `<li>${n} ${what}</li>` : '');
    const plural = (n: number) => (n === 1 ? '' : 's');

    let message = 'You are about to:<br/><ul>';
    if (adds.length > 0) {
      message += `<li><b>Add ${adds.length}</b> new Account User${plural(adds.length)}</li>`;
    }
    if (updates.length > 0) {
      message += `<li><b>Update ${updates.length}</b> existing Account User${plural(updates.length)}<ul>`;
      message += line(apiLinkCount, `will get an ApiLink`);
      message += line(hireDateCount, `will get a Hire Date`);
      message += line(shippingCount, `will move to a different Shipping Group`);
      message += line(programCount, `will join a new Program Group`);
      message += line(keptCount, `have a different ApiLink or Hire Date that will be <b>kept as-is</b>`);
      message += '</ul></li>';
    }
    message += line(noChange.length, `unchanged`);
    if (rejects.length > 0) {
      message += `<li><b>Skip ${rejects.length}</b> row${plural(rejects.length)} with errors</li>`;
    }
    message += '</ul>Proceed?';

    const ok = await this.dialogService.verifyProceedDialog('Proceed With Save?', message);
    if (ok) {
      await this.runImport();
    }
  }

  /**
   * Run the save. Adds and updates go in one Breeze transaction: a partly-applied upsert
   * would leave the account in a state nobody asked for, so all-or-nothing is the right
   * trade here and the admin is told so before submitting.
   */
  @wrapBusy
  private async runImport() {
    this.inFlight = true;
    this.importRunning = true;

    // Correlate save errors back to grid rows. Added rows are keyed by the generated
    // ProximityUser.id; updated rows by the AccountUser.id they matched.
    const rowsByKey = new Map<string, ImportUser>();

    this.importUsers.forEach((u) => {
      u.Outcome = u.Plan === 'Reject' ? 'Skipped' : u.Plan === 'No Change' ? 'No Change' : '';
    });

    try {
      const updateRows = this.importUsers.filter((u) => u.Plan === 'Update');
      const matchedIds = updateRows.map((u) => u.MatchedAccountUserId!).filter((id) => !!id);
      const accountUsers = await this.dbQueryService.getAccountUsersForImportUpdate(matchedIds);
      const byId = new Map(accountUsers.map((au) => [au.id, au]));

      this.importUsers
        .filter((u) => u.Plan === 'Add')
        .forEach((u) => {
          const proximityUserId = this.createNewUser(u);
          rowsByKey.set(proximityUserId, u);
        });

      updateRows.forEach((u) => {
        const au = byId.get(u.MatchedAccountUserId!);
        const ch = this.rowChanges.get(u.Id);
        if (!au || !ch) {
          u.Outcome = 'Failed';
          u.Details = 'Could not load the existing Account User to update';
          return;
        }
        this.applyUpdate(au, ch);
        rowsByKey.set(au.id, u);
      });

      try {
        await this.onSave();
        rowsByKey.forEach((u) => {
          u.Outcome = u.Plan === 'Add' ? 'Added' : 'Updated';
        });
      } catch (saveErr: any) {
        this.mapSaveErrorsToRows(saveErr, rowsByKey);
        // Breeze rolls the whole transaction back, so any row not named in the entity errors
        // failed too — it just failed without a message of its own.
        rowsByKey.forEach((u) => {
          if (!u.Outcome) {
            u.Outcome = 'Failed';
            u.Details = u.Details || 'Save failed (transaction rolled back)';
          }
        });
        throw saveErr;
      }

      this.updateColumnVisibility();
      this.gridOptions.apiX?.redrawRows();
      await this.showPostSaveSummary();
    } catch (ex) {
      this.updateColumnVisibility();
      this.gridOptions.apiX?.redrawRows();
      await this.showPostSaveSummary(ex);
    } finally {
      this.importRunning = false;
      this.inFlight = false;
    }
  }

  /**
   * Apply one row's changes to an existing Account User, writing a system note per change so
   * the person's history shows what the import did, the same way it shows a change made by
   * hand. Applies only what recomputePlans() recorded — it never re-derives the rules, so the
   * save cannot diverge from the summary the admin approved.
   */
  private applyUpdate(au: AccountUser, ch: RowChanges) {
    if (ch.setApiLink) {
      au.apiLink = ch.setApiLink;
      this.addUserNote(au, `ApiLink set by import: ${ch.setApiLink}`);
    }

    if (ch.setHireDate) {
      au.proximityUser.hireDate = ch.setHireDate;
      this.addUserNote(au, `Hire Date set by import: ${this.fmtDate(ch.setHireDate)}`);
    }

    if (ch.moveShippingTo) {
      const removedNames: string[] = [];
      // slice() first — deleting from the live Breeze collection while iterating it skips rows.
      au.shippingUserGroupMaps.slice().forEach((m) => {
        if (ch.removeShippingGroupIds.includes(m.shippingUserGroupId)) {
          removedNames.push(m.shippingUserGroup?.name ?? '');
          EntityFns.deleteOrDetach(m.entityAspect);
        }
      });
      const map = this.dbSaveService.createEntity(ShippingUserGroupMap, {
        shippingUserGroupId: ch.moveShippingTo.id,
        accountUserId: au.id,
      });
      map.entityAspect.setAdded();
      this.addUserNote(
        au,
        removedNames.length > 0
          ? `Shipping User Group changed by import: ${removedNames.join(', ')} → ${ch.moveShippingTo.name}`
          : `Added to Shipping User Group by import: ${ch.moveShippingTo.name}`,
      );
    }

    if (ch.joinProgram) {
      const map = this.dbSaveService.createEntity(ProgramUserGroupMap, {
        programUserGroupId: ch.joinProgram.id,
        accountUserId: au.id,
        joinDate: new Date(),
      });
      map.entityAspect.setAdded();
      // Same wording the Account User page uses, so the note history reads consistently
      // whether a person or the import made the change.
      this.addUserNote(au, `Added to Program User Group: ${ch.joinProgram.name}`);
    }
  }

  private addUserNote(au: AccountUser, note: string) {
    this.dbSaveService.createEntity(ProximityUserNote, {
      proximityUserId: au.proximityUserId,
      note: note,
      isSystemGenerated: true,
      noteTs: new Date(),
    });
  }

  /**
   * Walk the Breeze EntityErrorsException and mark the originating rows as Failed.
   * An error's entity can be a new ProximityUser/AccountUser, an existing AccountUser being
   * updated, or one of the group maps — all of which resolve to a key in rowsByKey.
   */
  private mapSaveErrorsToRows(err: any, rowsByKey: Map<string, ImportUser>) {
    const entityErrors = err?.entityErrors as
      | Array<{ entity?: any; errorMessage?: string; propertyName?: string }>
      | undefined;
    if (!entityErrors || entityErrors.length === 0) return;

    for (const ee of entityErrors) {
      const e = ee.entity;
      if (!e) continue;

      // For an added row the key is proximityUser.id, and createNewUser sets
      // AccountUser.id to the same value. For an updated row it is the AccountUser.id.
      const candidates = [e.id, e.accountUserId, e.proximityUserId].filter((k) => !!k);
      const key = candidates.find((k) => rowsByKey.has(k));
      if (!key) continue;

      const u = rowsByKey.get(key)!;
      u.Outcome = 'Failed';
      u.Details = (ee.propertyName ? `${ee.propertyName}: ` : '') + (ee.errorMessage || 'Save failed');
    }
  }

  /** One summary dialog with the breakdown and a Stay / Go Back choice. */
  private async showPostSaveSummary(saveErr?: any) {
    const count = (outcome: string) => this.importUsers.filter((u) => u.Outcome === outcome).length;
    const added = count('Added');
    const updated = count('Updated');
    const unchanged = count('No Change');
    const skipped = count('Skipped');
    const failed = count('Failed');
    const plural = (n: number) => (n === 1 ? '' : 's');

    let header: string;
    let message: string;

    if (saveErr && added === 0 && updated === 0) {
      header = 'Import Failed';
      message =
        `The import did not save. Nothing was committed.<br/><br/>` +
        `<b>${failed}</b> row${plural(failed)} failed at save time. ` +
        `<b>${skipped}</b> row${plural(skipped)} had errors and ${skipped === 1 ? 'was' : 'were'} skipped.` +
        `<br/><br/>Review the highlighted rows, fix the issues, and upload again.<br/><br/>` +
        `Go back to the user list?`;
    } else if (failed > 0 || skipped > 0) {
      header = 'Import Complete (with issues)';
      message =
        `Added: <b>${added}</b><br/>Updated: <b>${updated}</b><br/>Unchanged: <b>${unchanged}</b><br/>` +
        `Skipped (errors): <b>${skipped}</b><br/>Failed at save time: <b>${failed}</b><br/><br/>` +
        `Go back to the user list?`;
    } else {
      header = 'Import Complete';
      message =
        `Added: <b>${added}</b><br/>Updated: <b>${updated}</b><br/>Unchanged: <b>${unchanged}</b><br/><br/>` +
        `Go back to the user list?`;
    }

    const goBack = await this.dialogService.verifyProceedDialog(header, message);
    if (goBack) {
      this.navigateBack();
    }
    // Otherwise stay on page — the admin can review the colored grid.
  }

  /**
   * Build the entities for one new person. Returns the proximityUser.id so callers can map
   * save errors back to this grid row.
   */
  createNewUser(p: ImportUser): string {
    const proximityUser = this.dbSaveService.createEntity(ProximityUser, {
      activeStatusId: ActiveStatusEnum.Active,
      lastName: p.LastName,
      middleName: p.MiddleName,
      firstName: p.FirstName,
      salutation: p.Salutation,
      email: p.Email,
      username: p.UserName,
      hireDate: this.parseDate(p.HireDate),
      proximityRoleId: this.defaultAccountUserRoleId,
    });
    const accountUser = this.dbSaveService.createEntity(AccountUser, {
      id: proximityUser.id,
      accountId: this.accountId,
      proximityUserId: proximityUser.id,
      apiLink: p.ApiLink,
      genderId: this.getGenderId(p.Gender),
    });

    proximityUser.entityAspect.setAdded();
    accountUser.entityAspect.setAdded();

    // Gate on the resolved Id, NOT on the code column. Gating on the code built a map row
    // with a null foreign key whenever a spreadsheet carried a code for a dimension this
    // account does not map, and that rolled back the entire import.
    if (p.ShippingUserGroupId) {
      const map = this.dbSaveService.createEntity(ShippingUserGroupMap, {
        shippingUserGroupId: p.ShippingUserGroupId,
        accountUserId: proximityUser.id,
      });
      map.entityAspect.setAdded();
    }

    if (p.ProgramUserGroupId) {
      const map = this.dbSaveService.createEntity(ProgramUserGroupMap, {
        programUserGroupId: p.ProgramUserGroupId,
        accountUserId: proximityUser.id,
        joinDate: new Date(),
      });
      map.entityAspect.setAdded();
    }

    return proximityUser.id as string;
  }

  private getGenderId(gender: string): GenderEnum | undefined {
    const g = (gender || '').toUpperCase();
    if (g === 'M' || g === 'MALE') return GenderEnum.Male;
    if (g === 'F' || g === 'FEMALE') return GenderEnum.Female;
    return GenderEnum.NotSpecified;
  }

  // #endregion
}
